import { config } from '@iheartjane/config';
import {
  Box,
  ConditionalWrapper,
  Flex,
  JaneLogo,
  ReeferThemeProvider,
  Typography,
} from '@iheartjane/reefer';
import { trackError as trackErrorUtil } from '@iheartjane/tracking';
import type { PropsWithChildren, ReactNode } from 'react';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';

export interface ErrorPageProps {
  button?: ReactNode;
  error?: unknown;
  /** Whether to use legacy theme CSS. Non-legacy theme CSS uses CSS '@layer'. */
  legacy?: boolean;
  whiteLabel?: boolean;
  withReeferThemeProvider?: boolean;
}

export function ErrorPage({
  button,
  error,
  legacy = false,
  whiteLabel = true,
  withReeferThemeProvider = false,
}: ErrorPageProps) {
  const errorName = error instanceof Error ? error.name : (error as string);
  return (
    <ConditionalWrapper
      condition={withReeferThemeProvider}
      wrapper={(children) => (
        <ReeferThemeProvider legacy={legacy}>{children}</ReeferThemeProvider>
      )}
    >
      <Flex
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
        p={48}
      >
        {!whiteLabel && <JaneLogo size="lg" />}
        <Typography mb={24} variant="h1-regular">
          Oops!
        </Typography>
        <Typography mb={24}>
          Sorry, an unexpected error has occurred.
        </Typography>
        {button}
      </Flex>
      {config.dev && !!error && (
        <Box p={48}>
          <Typography my={24} variant="h1-regular">
            Error Details
          </Typography>
          <Box
            as="pre"
            background="grays-dark"
            p={24}
            style={{ color: 'white' }}
            width="100%"
          >
            {errorName && <code>Error Name: {errorName}</code>}
            <br />
            {error instanceof Error && error?.message && (
              <code>Error Message: {error.message}</code>
            )}
            <br />
            <br />
            {error instanceof Error && error?.stack && (
              <code>{error.stack}</code>
            )}
          </Box>
        </Box>
      )}
    </ConditionalWrapper>
  );
}

interface RouterErrorPageProps
  extends Omit<ErrorPageProps, 'error'>, PropsWithChildren {
  trackError?: (error: Error | string) => void;
}

export function RouterErrorPage({
  button,
  legacy = false,
  trackError = trackErrorUtil,
  whiteLabel = true,
  withReeferThemeProvider = false,
}: RouterErrorPageProps) {
  const error = useRouteError();
  useEffect(() => {
    trackError(error as Error);
  }, [error, trackError]);

  return (
    <ErrorPage
      button={button}
      error={error}
      legacy={legacy}
      whiteLabel={whiteLabel}
      withReeferThemeProvider={withReeferThemeProvider}
    />
  );
}
